import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupGlobalBackButtonHandlers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    // First check if the target is a summary, and get its direct parent details
    if (event.target.tagName === 'SUMMARY' || event.target.closest('summary')) {
      const summary = event.target.tagName === 'SUMMARY' ? event.target : event.target.closest('summary');
      const directDetails = summary?.parentElement;
      if (directDetails?.tagName === 'DETAILS') {
        console.log('Found direct details from summary:', directDetails.id);
        return directDetails;
      }
    }

    // Check if the event is from a submenu that was moved to body
    const submenu = event.target.closest('.menu-drawer__submenu');
    if (submenu && submenu.dataset.originalParent) {
      const originalDetails = document.getElementById(submenu.dataset.originalParent);
      if (originalDetails) return originalDetails;
    }

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    if (event?.preventDefault) event.preventDefault();
    if (event?.stopPropagation) event.stopPropagation();

    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');
    
    // If opening a submenu (not the main drawer), move it to body to escape parent constraints
    if (details.id !== 'Details-menu-drawer-container') {
      console.log('Opening submenu for details:', details.id);
      const submenu = details.querySelector('.menu-drawer__submenu');
      console.log('Found submenu:', submenu);
      console.log('Already moved?', submenu?.dataset.movedToBody);
      
      if (submenu && !submenu.dataset.movedToBody) {
        console.log('Moving submenu to body...');
        // Store original parent for restoration
        submenu.dataset.movedToBody = 'true';
        submenu.dataset.originalParent = details.id;
        
        // Always use the main drawer's position for ALL submenus (including nested)
        // Find the actual drawer element (it might be in the DOM or another submenu might reference it)
        let drawer = this.querySelector('.menu-drawer');
        
        // If we can't find the drawer in the header-drawer element, it might be because
        // we're in a nested scenario - find any moved submenu and use its position
        if (!drawer) {
          const anyMovedSubmenu = document.body.querySelector('.menu-drawer__submenu[data-moved-to-body="true"]');
          if (anyMovedSubmenu) {
            drawer = anyMovedSubmenu; // Use the first moved submenu's position
          }
        }
        
        const drawerRect = drawer ? drawer.getBoundingClientRect() : { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
        console.log('Using drawer position:', drawerRect);
        
        // Move to body and position it at the drawer's location
        document.body.appendChild(submenu);
        
        submenu.style.cssText = `
          position: fixed !important;
          top: ${drawerRect.top}px !important;
          left: ${drawerRect.left}px !important;
          width: ${drawerRect.width}px !important;
          height: ${drawerRect.height}px !important;
          z-index: 999 !important;
          background-color: var(--color-background) !important;
          transform: translateX(0) !important;
          visibility: visible !important;
          opacity: 1 !important;
          display: flex !important;
          flex-direction: column !important;
          pointer-events: auto !important;
          overflow-y: auto !important;
        `;
        
        console.log('Submenu moved and styled!');
        // Note: Back button and close button handlers are set up globally in #setupGlobalBackButtonHandlers
      }
    }
    
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    details.removeAttribute('open');
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    
    // Helper function to clean up a moved submenu
    const cleanupSubmenu = (submenu, originalDetails) => {
      // Clear all inline styles
      submenu.style.cssText = '';
      delete submenu.dataset.movedToBody;
      delete submenu.dataset.originalParent;
      
      // Move back to original parent
      if (originalDetails) {
        originalDetails.appendChild(submenu);
      }
    };
    
    // If closing the main drawer, clean up ALL moved submenus
    if (details.id === 'Details-menu-drawer-container') {
      const movedSubmenus = document.body.querySelectorAll('.menu-drawer__submenu[data-moved-to-body="true"]');
      movedSubmenus.forEach(submenu => {
        const originalParentId = submenu.dataset.originalParent;
        const originalDetails = originalParentId ? document.getElementById(originalParentId) : null;
        cleanupSubmenu(submenu, originalDetails);
      });
    }
    // If closing a submenu, move it back to its original parent
    else {
      const submenu = document.body.querySelector(`.menu-drawer__submenu[data-original-parent="${details.id}"]`);
      if (submenu && submenu.dataset.movedToBody) {
        cleanupSubmenu(submenu, details);
      }
    }

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Set up global event handlers for back buttons in moved submenus
   * This ensures back buttons work even when submenus are moved to document.body
   */
  #setupGlobalBackButtonHandlers() {
    // Global click handler for opening submenus (summary elements inside moved submenus)
    document.addEventListener('click', (e) => {
      // Check if clicking on a summary that would open a nested submenu
      const summary = e.target.closest('summary');
      if (!summary) return;
      
      // Must be inside a moved submenu
      const parentSubmenu = summary.closest('.menu-drawer__submenu');
      if (!parentSubmenu || !parentSubmenu.dataset.movedToBody) return;
      
      // Must be a summary that opens a submenu (not just a link)
      const details = summary.parentElement?.tagName === 'DETAILS' ? summary.parentElement : null;
      if (!details || details.id === 'Details-menu-drawer-container') return;
      
      // Must have a submenu to open
      const hasSubmenu = details.querySelector('.menu-drawer__submenu');
      if (!hasSubmenu) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      console.log('Opening nested submenu via global handler for:', details.id);
      
      // Call the open method with a fake event
      this.open({ target: summary, preventDefault: () => {}, stopPropagation: () => {} });
    }, true); // Use capture phase to ensure we catch it first
    
    // Global click handler for back buttons in moved submenus
    document.addEventListener('click', (e) => {
      const backButton = e.target.closest('.menu-drawer__back-button');
      if (!backButton) return;

      console.log('Back button clicked (global handler)');
      
      // Check if this back button is inside a moved submenu
      const submenu = backButton.closest('.menu-drawer__submenu');
      console.log('Submenu:', submenu);
      console.log('Moved to body?', submenu?.dataset.movedToBody);
      console.log('Original parent:', submenu?.dataset.originalParent);
      
      if (submenu && submenu.dataset.movedToBody && submenu.dataset.originalParent) {
        e.preventDefault();
        e.stopPropagation();
        
        console.log('Closing submenu via global handler...');
        
        // Find the original details element and close it
        const originalDetails = document.getElementById(submenu.dataset.originalParent);
        console.log('Found original details:', originalDetails);
        if (originalDetails) {
          this.#close(originalDetails);
        }
      }
    }, true); // Use capture phase to ensure we catch it first

    // Global click handler for close buttons (X) in moved submenus
    document.addEventListener('click', (e) => {
      const closeButton = e.target.closest('.menu-drawer__close-button');
      if (!closeButton) return;

      // Check if this close button is inside a moved submenu
      const submenu = closeButton.closest('.menu-drawer__submenu');
      if (submenu && submenu.dataset.movedToBody && submenu.dataset.originalParent) {
        e.preventDefault();
        e.stopPropagation();
        
        // Find the original details element and close it
        const originalDetails = document.getElementById(submenu.dataset.originalParent);
        if (originalDetails) {
          this.#close(originalDetails);
        }
      }
    }, true); // Use capture phase to ensure we catch it first
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
